import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDb } from '@data/db/database';
import { resolveActiveBusiness } from '@data/services/businessSession';
import { loadDocument, listRecentDocuments, type RecentDocumentsPage } from '@data/services/documentReader';
import { loadBusinessIdentityWithImages } from '@data/services/businessReader';
import type { BusinessIdentity } from '@core/doc/renderA4';
import {
  convertQuotationToSalesOrderDraft,
  createSalesOrderDraft,
  deleteSalesOrderDraft,
  salesOrderFormOptions,
  updateSalesOrderDraft,
  type SalesOrderInput,
} from '@data/services/salesOrderService';
import { currentBusinessId, DOCUMENTS_QUERY_ROOT } from '@features/documents/useDocuments';

export const SALES_ORDERS_QUERY_ROOT = ['sales-orders'] as const;

export function useSalesOrderOptions() {
  return useQuery({
    queryKey: [...SALES_ORDERS_QUERY_ROOT, 'options'],
    queryFn: async () => {
      const id = await currentBusinessId();
      return id ? salesOrderFormOptions(getDb() as never, id) : { customers: [], products: [] };
    },
  });
}

export function useSalesOrders() {
  return useQuery({
    queryKey: [...SALES_ORDERS_QUERY_ROOT, 'list'],
    queryFn: async (): Promise<RecentDocumentsPage> => {
      const id = await currentBusinessId();
      if (!id) return { documents: [], total: 0, truncated: false, partyNamesByDocumentId: new Map() };
      return listRecentDocuments(getDb() as never, id, 200, true, 'SALES_ORDER') as Promise<RecentDocumentsPage>;
    },
  });
}

export function useSalesOrder(documentId: string | null) {
  return useQuery({
    queryKey: [...SALES_ORDERS_QUERY_ROOT, 'document', documentId],
    enabled: documentId !== null,
    queryFn: async () => {
      if (!documentId) return null;
      const business = await resolveActiveBusiness(getDb() as never);
      const db = getDb();
      const stored = await db.documents.get(documentId);
      if (!stored || stored.docType !== 'SALES_ORDER' || stored.businessId !== business.id) {
        throw new Error('The requested document is not a sales order in the active business.');
      }
      const [model, businessIdentity, lines, links, children] = await Promise.all([
        loadDocument(db as never, documentId, { businessStateCode: business.stateCode, businessId: business.id }),
        loadBusinessIdentityWithImages(db as never, business.id),
        db.documentLines.where('documentId').equals(documentId).toArray(),
        db.documentLinks.where('fromDocumentId').equals(documentId).toArray(),
        db.documentLinks.where('toDocumentId').equals(documentId).toArray(),
      ]);
      const sourceLineByOrderLineId = new Map(links.filter((link) => link.relation === 'CONVERTED_FROM' && link.toLineId).map((link) => [link.fromLineId ?? '', link.toLineId as string]));
      return {
        model,
        stored,
        business: businessIdentity as BusinessIdentity,
        lines,
        sourceLineByOrderLineId,
        hasChildren: children.some((link) => link.fromDocumentId !== documentId),
      };
    },
  });
}

async function invalidateSalesOrders(client: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    client.invalidateQueries({ queryKey: DOCUMENTS_QUERY_ROOT }),
    client.invalidateQueries({ queryKey: SALES_ORDERS_QUERY_ROOT }),
    client.invalidateQueries({ queryKey: ['quotations'] }),
  ]);
}

export function useCreateSalesOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: SalesOrderInput) => createSalesOrderDraft(getDb() as never, input),
    onSuccess: () => invalidateSalesOrders(client),
  });
}

export function useConvertQuotationToSalesOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (quotationId: string) => convertQuotationToSalesOrderDraft(getDb() as never, { quotationId }),
    onSuccess: () => invalidateSalesOrders(client),
  });
}

export function useUpdateSalesOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Omit<SalesOrderInput, 'businessId' | 'quotationId'> }) =>
      updateSalesOrderDraft(getDb() as never, id, input),
    onSuccess: () => invalidateSalesOrders(client),
  });
}

export function useDeleteSalesOrder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSalesOrderDraft(getDb() as never, id),
    onSuccess: () => invalidateSalesOrders(client),
  });
}
